import type { FC, SVGProps } from 'react';
import type { UnitHeaderBadgeColors } from '../../../businessLogicService/StudentDashboardAux/getColorByUnit';

interface Props extends SVGProps<SVGSVGElement> {
  colors: UnitHeaderBadgeColors;
  // When muted, only the unit-colored chrome (ring/highlight/bubble outline) is desaturated; the
  // inner multicolor grid keeps its color.
  muted?: boolean;
}

// The Support badge: a circular disc with a multicolor "tools" grid and a count bubble at the bottom.
// The ring, top shine, and bubble outline take the selected unit's colors; the inner grid keeps its
// own multicolor fills. The count number is not drawn here — UnitSkillBadge overlays the real count.
export const UnitHeaderSupportIcon: FC<Props> = ({ colors, muted, ...svgProps }) => {
  const mutedClass = muted ? ' tw-grayscale' : '';
  return (
    <svg viewBox="0 0 55 66" fill="none" xmlns="http://www.w3.org/2000/svg" {...svgProps}>
      <path
        className={`${colors.ring}${mutedClass}`}
        d="M55 28C55 43.1878 42.6878 55.5 27.5 55.5C12.3122 55.5 0 43.1878 0 28C0 12.8122 12.3122 0.5 27.5 0.5C42.6878 0.5 55 12.8122 55 28Z"
      />
      <path
        className={`${colors.highlight}${mutedClass}`}
        d="M46.5385 8.008C37.8859 -0.156633 24.4671 -2.10522 13.7159 4.3649C1.85384 11.5036 -3.27237 25.414 2.17626 38.5769L46.5385 8.008Z"
      />
      <path
        d="M47.5966 28C47.5966 39.0988 38.5992 48.0961 27.5005 48.0961C16.4017 48.0961 7.4043 39.0988 7.4043 28C7.4043 16.9012 16.4017 7.90381 27.5005 7.90381C38.5992 7.90381 47.5966 16.9012 47.5966 28Z"
        fill="#FEFBF9"
      />
      <path
        d="M17.375 20.35C17.375 18.5351 17.375 17.6276 17.9187 17.0638C18.4624 16.5 19.3374 16.5 21.0875 16.5H22.9438C24.6938 16.5 25.5689 16.5 26.1126 17.0638C26.6563 17.6276 26.6563 18.5351 26.6563 20.35V22.275C26.6563 24.0899 26.6563 24.9974 26.1126 25.5612C25.5689 26.125 24.6938 26.125 22.9438 26.125H21.0875C19.3374 26.125 18.4624 26.125 17.9187 25.5612C17.375 24.9974 17.375 24.0899 17.375 22.275V20.35Z"
        fill="#42C5A6"
      />
      <path
        d="M28.3438 20.35C28.3438 18.5351 28.3438 17.6276 28.8874 17.0638C29.4311 16.5 30.3062 16.5 32.0563 16.5H33.9125C35.6626 16.5 36.5376 16.5 37.0813 17.0638C37.625 17.6276 37.625 18.5351 37.625 20.35V22.275C37.625 24.0899 37.625 24.9974 37.0813 25.5612C36.5376 26.125 35.6626 26.125 33.9125 26.125H32.0562C30.3062 26.125 29.4311 26.125 28.8874 25.5612C28.3438 24.9974 28.3438 24.0899 28.3438 22.275V20.35Z"
        fill="#0A9AF8"
      />
      <path
        d="M17.375 31.725C17.375 29.9101 17.375 29.0026 17.9187 28.4388C18.4624 27.875 19.3374 27.875 21.0875 27.875H22.9438C24.6938 27.875 25.5689 27.875 26.1126 28.4388C26.6563 29.0026 26.6563 29.9101 26.6563 31.725V33.65C26.6563 35.4649 26.6563 36.3724 26.1126 36.9362C25.5689 37.5 24.6938 37.5 22.9438 37.5H21.0875C19.3374 37.5 18.4624 37.5 17.9187 36.9362C17.375 36.3724 17.375 35.4649 17.375 33.65V31.725Z"
        fill="#FDB814"
      />
      <path
        d="M28.3438 31.725C28.3438 29.9101 28.3438 29.0026 28.8874 28.4388C29.4311 27.875 30.3062 27.875 32.0563 27.875H33.9125C35.6626 27.875 36.5376 27.875 37.0813 28.4388C37.625 29.0026 37.625 29.9101 37.625 31.725V33.65C37.625 35.4649 37.625 36.3724 37.0813 36.9362C36.5376 37.5 35.6626 37.5 33.9125 37.5H32.0562C30.3062 37.5 29.4311 37.5 28.8874 36.9362C28.3438 36.3724 28.3438 35.4649 28.3438 33.65V31.725Z"
        fill="#A6A6A6"
      />
      <mask
        id="path-8-outside-1_13105_32434"
        maskUnits="userSpaceOnUse"
        x="9"
        y="42"
        width="38"
        height="24"
        fill="black"
      >
        <rect fill="white" x="9" y="42" width="38" height="24" />
        <path d="M11 50C11 46.6863 13.6863 44 17 44H39C42.3137 44 45 46.6863 45 50V56C45 59.3137 42.3137 62 39 62H17C13.6863 62 11 59.3137 11 56V50Z" />
      </mask>
      <path
        d="M11 50C11 46.6863 13.6863 44 17 44H39C42.3137 44 45 46.6863 45 50V56C45 59.3137 42.3137 62 39 62H17C13.6863 62 11 59.3137 11 56V50Z"
        fill="#FEFBF9"
      />
      <path
        className={`${colors.bubbleBorder}${mutedClass}`}
        d="M9 50C9 45.5817 12.5817 42 17 42H39C43.4183 42 47 45.5817 47 50H43C43 47.7909 41.2091 46 39 46H17C14.7909 46 13 47.7909 13 50H9ZM47 58C47 62.4183 43.4183 66 39 66H17C12.5817 66 9 62.4183 9 58L13 56C13 57.1046 14.7909 58 17 58H39C41.2091 58 43 57.1046 43 56L47 58ZM17 66C12.5817 66 9 62.4183 9 58V50C9 45.5817 12.5817 42 17 42V46C14.7909 46 13 47.7909 13 50V56C13 57.1046 14.7909 58 17 58V66ZM39 42C43.4183 42 47 45.5817 47 50V58C47 62.4183 43.4183 66 39 66V58C41.2091 58 43 57.1046 43 56V50C43 47.7909 41.2091 46 39 46V42Z"
        mask="url(#path-8-outside-1_13105_32434)"
      />
    </svg>
  );
};
